/**
 * Vault Types: Project document storage system
 */

// ─── Visibility ──────────────────────────────────────────────────────────────
export type VaultVisibility = 'public' | 'private' | 'shared';

// ─── Folder Colors ───────────────────────────────────────────────────────────
export const VAULT_FOLDER_COLORS = [
  { hex: '#3b82f6', name: 'Blue' },
  { hex: '#ef4444', name: 'Red' },
  { hex: '#22c55e', name: 'Green' },
  { hex: '#f59e0b', name: 'Amber' },
  { hex: '#8b5cf6', name: 'Purple' },
  { hex: '#ec4899', name: 'Pink' },
  { hex: '#06b6d4', name: 'Cyan' },
  { hex: '#f97316', name: 'Orange' },
  { hex: '#6366f1', name: 'Indigo' },
  { hex: '#6b7280', name: 'Gray' },
] as const;

export type VaultFolderColor = (typeof VAULT_FOLDER_COLORS)[number]['hex'];

// ─── Core Types ──────────────────────────────────────────────────────────────
export interface VaultFolder {
  id: string;
  project_id: string;
  parent_folder_id: string | null;
  name: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  visibility: VaultVisibility;
  color: string;
  // Virtual fields (computed client-side)
  file_count?: number;
  subfolder_count?: number;
}

export interface VaultFile {
  id: string;
  project_id: string;
  folder_id: string | null;
  name: string;
  storage_path: string;
  size_bytes: number;
  mime_type: string | null;
  uploaded_by: string | null;
  created_at: string;
  updated_at: string;
  // Joined fields
  uploader_name?: string;
}

export interface VaultFolderShare {
  id: string;
  folder_id: string;
  shared_with_user_id: string;
  shared_by: string | null;
  created_at: string;
  // Joined
  shared_with_name?: string;
  shared_with_email?: string;
}

export interface VaultUser {
  id: string;
  full_name: string | null;
  email: string | null;
  avatar_url?: string | null;
}

// ─── Sorting ─────────────────────────────────────────────────────────────────
export type VaultSortField = 'name' | 'size_bytes' | 'created_at' | 'updated_at';
export type VaultSortDir = 'asc' | 'desc';

// ─── Navigation ──────────────────────────────────────────────────────────────
export interface VaultBreadcrumb {
  id: string | null; // null = root
  name: string;
}

// ─── Upload ──────────────────────────────────────────────────────────────────
export interface UploadProgress {
  fileIndex: number;
  fileName: string;
  fileSize: number;
  progress: number; // 0-100
  status: 'pending' | 'uploading' | 'complete' | 'error';
  error?: string;
}

// ─── Create Folder Options ───────────────────────────────────────────────────
export interface CreateFolderOptions {
  name: string;
  parentId?: string | null;
  visibility?: VaultVisibility;
  color?: string;
  sharedWith?: string[]; // user IDs
}

// ─── File Categories ─────────────────────────────────────────────────────────
export type FileCategory = 'image' | 'pdf' | 'document' | 'spreadsheet' | 'archive' | 'cad' | 'video' | 'audio' | 'other';

export function getFileCategory(mimeType: string | null, fileName: string): FileCategory {
  if (!mimeType && !fileName) return 'other';

  const mime = (mimeType || '').toLowerCase();
  const ext = fileName.split('.').pop()?.toLowerCase() || '';

  if (mime.startsWith('image/')) return 'image';
  if (mime === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if (['doc', 'docx', 'odt', 'rtf', 'txt', 'md'].includes(ext)) return 'document';
  if (['xls', 'xlsx', 'csv', 'ods'].includes(ext)) return 'spreadsheet';
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return 'archive';
  if (['dwg', 'dxf', 'skp', 'ifc', 'rvt'].includes(ext)) return 'cad';
  if (mime.includes('word') || mime.includes('document')) return 'document';
  if (mime.includes('spreadsheet') || mime.includes('excel')) return 'spreadsheet';
  if (mime.includes('zip') || mime.includes('compressed')) return 'archive';

  return 'other';
}

/**
 * Human-readable file size
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, i);
  return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}
